import { CandidatePriority } from '../candidate/candidate-priority';
import { CareType } from '../employer/care-type';
import { EmployerStatus, LOCATION_PAUSED_STATUSES, LOCATION_PUBLISHED_STATUSES } from '../employer/employer-status';
import { JobTitle } from '../job-title';
import { JobTitleEnum } from '../job-title/job-titles.enum';
import { JobPositionStatus } from './job-position-status';

export interface IsInitiativeJobPositionInput {
  status?: JobPositionStatus | null;
  employer?: {
    allowInitiativeApplications?: boolean;
    status?: EmployerStatus | null;
    careType?: CareType | null;
    organisation?: {
      allowInitiativeApplicationsPaused?: boolean;
    } | null;
  } | null;
  jobTitles?: Pick<JobTitle, 'title' | 'deputy' | 'trackingPriority'>[] | null;
}

/**
 * A priority job titles that are allowed to show as IB for non-hospital employers.
 * Based on the IB Job Titles list: red column = true means should show as IB.
 */
export const A_PRIORITY_IB_ALLOWLIST: { title: string; deputy: boolean }[] = [
  { title: JobTitleEnum.Instructor, deputy: false },
  { title: JobTitleEnum.Instructor, deputy: true },
  { title: JobTitleEnum.PracticeManager, deputy: false },
  { title: JobTitleEnum.QualityManagement, deputy: false },
  { title: JobTitleEnum.NursingSupervisor, deputy: true },
  { title: JobTitleEnum.UnitManager, deputy: true },
  { title: JobTitleEnum.TeamLeader, deputy: false },
  { title: JobTitleEnum.TeamLeader, deputy: true },
  { title: JobTitleEnum.ResidentialSectorManager, deputy: false },
  { title: JobTitleEnum.ResidentialSectorManager, deputy: true },
];

/**
 * Determines if a job position should be blocked from showing as Initiative Bewerbung
 * based on A priority job title rules.
 *
 * Rules:
 * - A priority vacancies for hospital employers should NOT show in IB at ALL
 * - A priority vacancies for non-hospital employers should only show if the job title/deputy combo is in the allowlist
 *
 * @param jobPosition - The job position to check
 * @returns true if the job position should be blocked from IB, false otherwise
 */
export const isJobPositionBlockedFromInitiative = (jobPosition: IsInitiativeJobPositionInput): boolean => {
  const { jobTitles = [], employer } = jobPosition;
  if (!jobTitles?.length) return false;
  const isHospital = employer?.careType === CareType.Hospital;
  return jobTitles.some(jt => {
    // We only care about Priority A
    if (jt.trackingPriority !== CandidatePriority.A) return false;
    // If it's a hospital and we found an 'A', it's blocked immediately
    if (isHospital) return true;
    // Otherwise, check allowlist
    const inAllowlist = A_PRIORITY_IB_ALLOWLIST.some(allowed => allowed.title === jt.title && allowed.deputy === (jt.deputy ?? false));
    return !inAllowlist;
  });
};

const isEmployerQualifiedForInitiative = (employer?: IsInitiativeJobPositionInput['employer']): boolean => {
  if (!employer?.status) return false;

  if (LOCATION_PUBLISHED_STATUSES.includes(employer.status) && employer.allowInitiativeApplications) {
    return true;
  }

  if (
    LOCATION_PAUSED_STATUSES.includes(employer.status) &&
    employer.allowInitiativeApplications &&
    employer.organisation?.allowInitiativeApplicationsPaused
  ) {
    return true;
  }

  return false;
};

/**
 * Determines if a job position qualifies as an initiative application.
 *
 * A job position is an initiative if:
 * 1. The job position status is Paused
 * 2. The job position is not blocked by A priority rules (hospital or disallowed job titles)
 * 3. Either:
 *    - The employer is published AND allows initiative applications, OR
 *    - The employer is paused AND allows initiative applications AND the organisation allows paused initiative applications
 *
 * @param jobPosition - The job position to check
 * @returns true if the job position is an initiative, false otherwise
 */
export const isJobPositionInitiative = (jobPosition: IsInitiativeJobPositionInput): boolean => {
  if (jobPosition.status !== JobPositionStatus.Paused) {
    return false;
  }

  // Block A priority job positions based on employer type and job title rules
  if (isJobPositionBlockedFromInitiative(jobPosition)) {
    return false;
  }

  return isEmployerQualifiedForInitiative(jobPosition.employer);
};

/**
 * Determines if a job position is initiative after the funnel is completed (on the potential jobs step).
 *
 * A job position is an initiative if:
 * 1. The job position status is different from Published
 * 2. The job position is not blocked by A priority rules (hospital or disallowed job titles)
 * 3. Either:
 *    - The employer is published AND allows initiative applications, OR
 *    - The employer is paused AND allows initiative applications AND the organisation allows paused initiative applications
 *
 * @param jobPosition - The job position to check
 * @returns true if the job position is an initiative, false otherwise
 */
export const isPotentialJobPositionInitiative = (jobPosition: IsInitiativeJobPositionInput): boolean => {
  if (jobPosition.status === JobPositionStatus.Published) {
    return false;
  }

  // Block A priority job positions based on employer type and job title rules
  if (isJobPositionBlockedFromInitiative(jobPosition)) {
    return false;
  }

  return isEmployerQualifiedForInitiative(jobPosition.employer);
};
